import type {
  Equation,
  MatrixNode,
} from "@src/features/equation-editor/equation";
import type { CSSProperties, DragEvent, KeyboardEvent, ReactNode } from "react";
import {
  MAX_EXPRESSION_SOURCE_LENGTH,
  MAX_MATRIX_DURATION_MS,
  MIN_MATRIX_DURATION_MS,
} from "@src/features/equation-editor/equation-policy";
import { createContext, useContext } from "react";
import {
  groupEntriesByColumn,
  renderEntryColumnTemplate,
} from "../../lib/equation-layout";
import type { AnimationProgress } from "@src/features/playback/animation";
import { DiagnosticField } from "./DiagnosticField";
import type { EquationViewModel } from "@src/features/equation-editor/lib/view-model";
import type { Translate } from "@src/i18n/translate";
import { diagnosticMessage } from "../../lib/diagnostic-message";
import { getEquationViewModel } from "@src/features/equation-editor/lib/view-model";
import { initializeReorderDrag, isInteractiveDragTarget } from "../../lib/drag";
import { useInteractiveDragSuppression } from "../../hooks/use-interactive-drag-suppression";
import type { Dimension } from "@src/math/types";

const MatrixAnimationProgressContext = createContext<AnimationProgress | null>(
  null,
);

export function MatrixAnimationProgressProvider({
  progress,
  children,
}: {
  progress: AnimationProgress | null;
  children: ReactNode;
}) {
  return (
    <MatrixAnimationProgressContext.Provider value={progress}>
      {children}
    </MatrixAnimationProgressContext.Provider>
  );
}

export function MatrixCard<D extends Dimension>({
  matrix,
  equation,
  viewModel = getEquationViewModel(equation),
  t,
  onDelete,
  onEntryChange,
  onDurationChange,
  onKeyDown,
  onDragStart,
  dragging,
  dropPosition,
  onDragOver,
  onDragEnd,
  onDrop,
}: {
  matrix: MatrixNode<D>;
  equation: Equation<D>;
  viewModel?: EquationViewModel;
  t: Translate;
  onDelete: () => void;
  onEntryChange: (id: string, index: number, value: string) => boolean;
  onDurationChange: (id: string, durationMs: number) => boolean;
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
  onDragStart: () => void;
  dragging: boolean;
  dropPosition?: "before" | "after" | undefined;
  onDragOver: (e: DragEvent<HTMLElement>) => void;
  onDragEnd: () => void;
  onDrop: (e: DragEvent<HTMLElement>) => void;
}) {
  const columns = equation.dimension;
  const {
    draggable: cardDraggable,
    onPointerDownCapture,
    stopSuppressingDrag,
  } = useInteractiveDragSuppression();
  const durationId = `matrix-${matrix.id}-duration`;
  const template = renderEntryColumnTemplate(
    groupEntriesByColumn(matrix.entries, columns),
  );
  const style = {
    gridTemplateColumns: template,
  } as CSSProperties;

  return (
    <li
      className="matrix-item"
      draggable={cardDraggable}
      tabIndex={0}
      aria-label={t("matrix", { label: matrix.label })}
      aria-keyshortcuts="Alt+ArrowLeft Alt+ArrowRight"
      aria-describedby="reorder-instructions"
      data-dragging={enabledAttribute(dragging)}
      data-drop-position={dropPosition}
      onKeyDown={onKeyDown}
      onPointerDownCapture={onPointerDownCapture}
      onPointerUpCapture={stopSuppressingDrag}
      onPointerCancelCapture={stopSuppressingDrag}
      onLostPointerCapture={stopSuppressingDrag}
      onDragStart={(event) => {
        if (!cardDraggable || isInteractiveDragTarget(event.target)) {
          event.preventDefault();
          return;
        }
        initializeReorderDrag(event.dataTransfer);
        onDragStart();
      }}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
    >
      <AnimatedMatrixCard
        matrixId={matrix.id}
        dimension={equation.dimension}
        label={t("matrix", { label: matrix.label })}
      >
        <header className="matrix-header">
          <math className="matrix-label" aria-label={matrix.label}>
            <mi>{matrix.label}</mi>
          </math>
          <div className="matrix-actions">
            <button
              className="equation-delete-button"
              type="button"
              onClick={onDelete}
              aria-label={t("deleteMatrix", {
                label: matrix.label,
              })}
              title={t("deleteMatrix", { label: matrix.label })}
            >
              ×
            </button>
          </div>
        </header>
        <div
          className="matrix-expression"
          role="group"
          aria-label={t("matrixValues", { label: matrix.label })}
        >
          <div className="matrix-bracket">
            <div
              className={`matrix-grid matrix-grid-${String(equation.dimension)}`}
              style={style}
              role="group"
              aria-label={t("matrixEntries", {
                label: matrix.label,
              })}
            >
              {matrix.entries.map((entry, index) => {
                const inputId = `matrix-${matrix.id}-entry-${String(index)}`;
                const diagnostics = viewModel.diagnosticsFor(
                  matrix.id,
                  "matrix-entry",
                  index,
                );
                const messages =
                  diagnostics.length > 0
                    ? diagnostics.map((diagnostic) =>
                        diagnosticMessage(t, diagnostic.code),
                      )
                    : [];
                return (
                  <DiagnosticField
                    key={index}
                    id={inputId}
                    messages={messages}
                    indicatorLabel={t("inputErrors")}
                  >
                    {(invalid, describedBy) => (
                      <input
                        id={inputId}
                        name={inputId}
                        type="text"
                        inputMode="text"
                        autoComplete="off"
                        maxLength={MAX_EXPRESSION_SOURCE_LENGTH}
                        value={entry}
                        data-entry-index={index}
                        aria-invalid={invalid || undefined}
                        aria-describedby={describedBy}
                        aria-label={t("matrixEntry", {
                          label: matrix.label,
                          row: Math.floor(index / columns) + 1,
                          column: (index % columns) + 1,
                        })}
                        onChange={(event) =>
                          onEntryChange(matrix.id, index, event.target.value)
                        }
                      />
                    )}
                  </DiagnosticField>
                );
              })}
            </div>
          </div>
        </div>
        <label className="duration-row" htmlFor={durationId}>
          <span>{matrix.durationMs}ms</span>
          <input
            id={durationId}
            type="range"
            min={MIN_MATRIX_DURATION_MS}
            max={MAX_MATRIX_DURATION_MS}
            step="100"
            value={matrix.durationMs}
            aria-label={t("matrixDuration", {
              label: matrix.label,
            })}
            onChange={(event) =>
              onDurationChange(matrix.id, Number(event.target.value))
            }
          />
        </label>
      </AnimatedMatrixCard>
    </li>
  );
}

function AnimatedMatrixCard({
  matrixId,
  dimension,
  label,
  children,
}: {
  matrixId: string;
  dimension: Dimension;
  label: string;
  children: ReactNode;
}) {
  const progress = useContext(MatrixAnimationProgressContext);
  const active = isActiveMatrix(progress, matrixId);
  const amount = progress !== null && active ? progress.progress : 0;
  return (
    <article
      className={`matrix-card matrix-card-${String(dimension)}`}
      data-active-step={enabledAttribute(active)}
      aria-current={active ? "step" : undefined}
      aria-label={label}
      style={
        {
          "--step-progress": `${String(amount * 100)}%`,
        } as CSSProperties
      }
    >
      {children}
    </article>
  );
}

function isActiveMatrix(
  progress: AnimationProgress | null,
  matrixId: string,
): boolean {
  return (
    progress?.mode === "composed" ||
    (progress?.mode === "steps" && progress.matrixId === matrixId)
  );
}

function enabledAttribute(enabled: boolean): true | undefined {
  return enabled ? true : undefined;
}
